import { useState, useRef } from "react";
import { PrimaryButton } from "src/components/Buttons";
import {
  DefaultContainer,
  SelectedContainer,
  DisabledContainer,
  LeftContainer,
  PrimaryLenderChoiceButton,
  LenderLogo,
  LenderTitle,
  RightContainer,
  TimeStamp,
  Monogram,
  EllipsesMenuButton,
  EllipsesDropdownMenu,
  EllipsesDropdownRow,
  EllipsesContainer,
} from "./LenderRow.styles";
import { useClickOutsideModalListener } from "src/hooks/useClickOutsideModalListener";
import { ApplicationStatusBadge } from "src/components/ApplicationStatusBadge";
import type { AppMilestoneType } from "src/components/ApplicationStatusBadge";
import { timeAgo } from "src/utils";
import { APPLICATION_STATUSES, APPLICATION_STATUS_KEYS } from "src/constants";
import {
  EllipseVerticalIcon,
  DownloadIcon,
  ArrowReloadHorizontalIcon,
  StarFilledIcon,
  StarOutlineIcon,
  UploadCircleIcon,
  FilePDFIcon,
} from "src/assets/icons";
import {
  DownloadCircle02Icon,
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@porchpass/ui";
import type { DtoSubmitApplicationToLenderResult } from "@porchpass/api-client";
import Dropdown from "src/components/Dropdown";
import { useLenderDocuments } from "src/hooks/useLenderDocuments";
import { useApplication } from "src/pages/ApplicationDetailPage/ApplicationContext";

interface OptionsType {
  menu: string[];
  actions: string[];
  editStatus: boolean;
  submitButton: boolean;
  disabled: boolean;
  selected: boolean;
}

const statusStates: { [key: string]: OptionsType } = {
  [APPLICATION_STATUS_KEYS.Invited]: {
    menu: [],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS["Ready to submit"]]: {
    menu: [],
    actions: ["view-application"],
    editStatus: false,
    submitButton: true,
    disabled: false,
    selected: true,
  },
  [APPLICATION_STATUS_KEYS.Submitted]: {
    menu: ["re-submit", "download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS["Pre-approved"]]: {
    menu: ["download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS["Clear to close"]]: {
    menu: ["download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS.Closed]: {
    menu: ["download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS.Funded]: {
    menu: ["download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: false,
    selected: false,
  },
  [APPLICATION_STATUS_KEYS.Declined]: {
    menu: ["download"],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: true,
    selected: false,
  },
  __NONE__: {
    menu: [],
    actions: [],
    editStatus: true,
    submitButton: false,
    disabled: true,
    selected: false,
  },
};
export default ({
  lenderId,
  logoUrl,
  title,
  date,
  appStatus,
  lenderStatus,
  state,
  isPrimaryLender,
  onPrimaryLenderSet,
  onLenderSubmit,
  isCurrentlySubmitting,
  isHighlighted = false,
  statusUpdatedAt,
  submitResult,
}: {
  lenderId: string;
  logoUrl?: string;
  title: string;
  date?: string;
  appStatus: string;
  lenderStatus: string;
  state?: string;
  isPrimaryLender?: boolean;
  onPrimaryLenderSet: () => void;
  onLenderSubmit?: () => void;
  isCurrentlySubmitting?: boolean;
  isHighlighted?: boolean;
  statusUpdatedAt?: string;
  submitResult?: DtoSubmitApplicationToLenderResult;
}) => {
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const ellipsisMenuRef = useRef(null);
  const { application } = useApplication();
  const {
    hasLenderDocuments,
    loading: isDocumentLoading,
    downloadLenderDocument,
  } = useLenderDocuments(application?.application_id || "", lenderId);

  useClickOutsideModalListener(ellipsisMenuRef, () => {
    setShowMenu(false);
  });

  const handleDownloadLenderDocument = async () => {
    await downloadLenderDocument(
      `${title}-application-${application?.application_id}.pdf`,
    );
  };
  const isResubmitAllowed = (): boolean => {
    if (!statusUpdatedAt) return false;
    if (lenderId === "porchpass") return false; // PorchPass can never resubmit
    if (lenderStatus === "INACTIVE") return false; // Inactive lenders cannot resubmit

    const lastUpdated = new Date(statusUpdatedAt);
    const now = new Date();
    const hoursSinceUpdate =
      (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);

    return hoursSinceUpdate >= 1;
  };

  const isSubmitAllowed = (): boolean => {
    if (lenderStatus === "INACTIVE") return false; // Inactive lenders cannot submit
    return true;
  };

  const optionState: OptionsType =
    statusStates[appStatus] || statusStates["__NONE__"];
  let Container = DefaultContainer;
  if (optionState.selected) Container = SelectedContainer;
  if (state === "disabled") Container = DisabledContainer;
  return (
    <Container
      key={title}
      state={(state as "disabled" | "selected" | undefined) ?? null}
      isHighlighted={isHighlighted}
      onMouseEnter={() => setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      <LeftContainer>
        <PrimaryLenderChoiceButton
          type="button"
          show={isRowHovered || !!isPrimaryLender}
          isSelected={!!isPrimaryLender}
          onClick={onPrimaryLenderSet}
        >
          {isPrimaryLender ? <StarFilledIcon /> : <StarOutlineIcon />}
        </PrimaryLenderChoiceButton>
        {logoUrl ? (
          <LenderLogo>
            <img src={logoUrl} alt={`${title} logo`} />
          </LenderLogo>
        ) : (
          <Monogram>{title.charAt(0)}</Monogram>
        )}
        <LenderTitle>{title}</LenderTitle>
      </LeftContainer>
      <RightContainer>
        {submitResult && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center">
                  {submitResult.success ? (
                    <CheckmarkCircle02Icon
                      variant="solid-standard"
                      size={18}
                      className="text-[#16a34a]"
                    />
                  ) : (
                    <AlertCircleIcon
                      variant="solid-standard"
                      size={18}
                      className="text-[#d97706]"
                    />
                  )}
                </span>
              </TooltipTrigger>
              {submitResult.error && (
                <TooltipContent side="top">
                  <p className="text-xs max-w-[240px]">{submitResult.error}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
        {date && <TimeStamp>{timeAgo(date)}</TimeStamp>}
        {optionState.actions.length > 0 && hasLenderDocuments && (
          <Dropdown
            displayText="Actions"
            menuItemsObj={{
              view: { label: "View Application", icon: <FilePDFIcon /> },
            }}
            selectedItem=""
            onSelectedItemChange={() => {
              if (
                hasLenderDocuments &&
                optionState.actions.includes("view-application")
              ) {
                void downloadLenderDocument(
                  `application-${application?.application_id}.pdf`,
                  true,
                );
              }
            }}
            hideCheckmark
          />
        )}
        {optionState.editStatus && (
          <ApplicationStatusBadge
            status={
              APPLICATION_STATUSES[
                appStatus as keyof typeof APPLICATION_STATUSES
              ] as AppMilestoneType
            }
          />
        )}
        {title === "Patriot Act Form" && hasLenderDocuments && (
          <PrimaryButton
            onClick={() =>
              void downloadLenderDocument(
                `application-${application?.application_id}-patriot-act-form.pdf`,
                false,
              )
            }
          >
            Download form
            <DownloadCircle02Icon />
          </PrimaryButton>
        )}

        {title === "Titan Credit Disclosure" && hasLenderDocuments && (
          <PrimaryButton
            onClick={() =>
              void downloadLenderDocument(
                `application-${application?.application_id}-titan-credit-disclosure.pdf`,
                false,
              )
            }
          >
            Download form
            <DownloadCircle02Icon />
          </PrimaryButton>
        )}

        {title !== "Patriot Act Form" &&
          title !== "Titan Credit Disclosure" &&
          optionState.submitButton &&
          isSubmitAllowed() && (
            <PrimaryButton
              onClick={onLenderSubmit}
              loading={isCurrentlySubmitting}
            >
              Submit to lender
              <UploadCircleIcon />
            </PrimaryButton>
          )}
        {((optionState.menu.includes("download") && hasLenderDocuments) ||
          (optionState.menu.includes("re-submit") && isResubmitAllowed())) && (
          <EllipsesContainer>
            <EllipsesMenuButton onClick={() => setShowMenu(true)}>
              <EllipseVerticalIcon />
            </EllipsesMenuButton>
            <EllipsesDropdownMenu ref={ellipsisMenuRef} show={showMenu}>
              {optionState.menu.includes("re-submit") && (
                <EllipsesDropdownRow
                  disabled={!isResubmitAllowed() || isCurrentlySubmitting}
                  loading={isCurrentlySubmitting}
                  onClick={onLenderSubmit}
                >
                  <ArrowReloadHorizontalIcon /> Re-submit
                </EllipsesDropdownRow>
              )}
              {optionState.menu.includes("download") && (
                <EllipsesDropdownRow
                  disabled={isDocumentLoading}
                  loading={isDocumentLoading}
                  onClick={() => {
                    void handleDownloadLenderDocument();
                  }}
                >
                  <DownloadIcon /> Download .pdf
                </EllipsesDropdownRow>
              )}
            </EllipsesDropdownMenu>
          </EllipsesContainer>
        )}
      </RightContainer>
    </Container>
  );
};
